import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Calendar from 'react-calendar';
import { format, parseISO, isSameDay } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
    MdAdd, MdCheckCircle, MdDelete, MdEdit, MdCalendarToday,
    MdClose, MdInfoOutline, MdChevronLeft, MdChevronRight, MdWarning
} from 'react-icons/md';

import { remindersAPI } from 'services/api';
import { useAuth } from 'context/AuthContext';

// --- SKELETON LOADER ---
const RemindersSkeleton = () => (
    <div className="flex w-full flex-col gap-6 animate-pulse mt-5">
        {/* Üst Bilgi Alanı */}
        <div className="h-16 w-full rounded-2xl bg-gray-200 dark:bg-navy-700" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Takvim İskeleti */}
            <div className="lg:col-span-4 h-[400px] rounded-3xl bg-gray-200 dark:bg-navy-700" />

            {/* Liste İskeleti */}
            <div className="lg:col-span-8 flex flex-col gap-4">
                <div className="h-12 w-1/3 rounded-xl bg-gray-200 dark:bg-navy-700" />
                {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-24 w-full rounded-2xl bg-gray-200 dark:bg-navy-700" />
                ))}
            </div>
        </div>
    </div>
);

const CommonReminders = () => {
    const { user } = useAuth();

    // --- TEMEL STATELER ---
    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [date, setDate] = useState(new Date());

    // --- MODAL & FORM STATELERİ ---
    const [modalVisible, setModalVisible] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentReminder, setCurrentReminder] = useState({
        id: null,
        title: '',
        description: '',
        due_date: format(new Date(), 'yyyy-MM-dd'),
    });

    // --- YÜKLEME (LOADER) STATELERİ ---
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [actionLoadingId, setActionLoadingId] = useState(null); // Spesifik buton loader'ı için

    // --- SAYFALAMA (PAGINATION) STATELERİ ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    // --- VERİ ÇEKME (Work-Logic) ---
    const fetchReminders = useCallback(async () => {
        setLoading(true);
        try {
            // Tüm hatırlatıcıları çekiyoruz (API'niz rol bazlı filtrelemeyi backend'de yapıyor varsayımıyla)
            const response = await remindersAPI.getAll();
            setReminders(response.reminders || []);
            setError('');
        } catch (err) {
            console.error("Hatırlatıcılar yüklenemedi:", err);
            setError("Veriler yüklenirken bir sorun oluştu.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchReminders();
    }, [fetchReminders]);

    // --- HESAPLANAN VERİLER (Memoized) ---

    // Seçili tarihe ait hatırlatıcılar
    const selectedDateReminders = useMemo(() => {
        if (!reminders.length) return [];
        return reminders.filter((r) => {
            if (!r.due_date) return false;
            return isSameDay(parseISO(r.due_date.split('T')[0]), date);
        });
    }, [reminders, date]);

    // Sayfalama Algoritması
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = selectedDateReminders.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(selectedDateReminders.length / itemsPerPage);

    // Takvimde gün değiştiğinde sayfayı 1'e sıfırla
    useEffect(() => {
        setCurrentPage(1);
    }, [date]);

    // Takvim üzerindeki işaretler (Noktalar)
    const tileContent = ({ date: tileDate, view }) => {
        if (view === 'month') {
            const hasReminder = reminders.some(
                (r) => r.due_date && isSameDay(parseISO(r.due_date.split('T')[0]), tileDate) && r.status !== 'completed'
            );
            return hasReminder ? (
                <div className="mx-auto mt-1 h-1.5 w-1.5 rounded-full bg-brand-500 dark:bg-brand-400 shadow-[0_0_5px_rgba(67,24,255,0.5)]"></div>
            ) : null;
        }
        return null;
    };

    // --- AKSİYON İŞLEYİCİLERİ ---

    const handleDateChange = (newDate) => {
        setDate(newDate);
    };

    const handleOpenModal = (reminder = null) => {
        if (reminder) {
            setIsEditing(true);
            setCurrentReminder({
                id: reminder.id,
                title: reminder.title || '',
                description: reminder.description || '',
                due_date: reminder.due_date ? reminder.due_date.split('T')[0] : format(date, 'yyyy-MM-dd'),
            });
        } else {
            setIsEditing(false);
            setCurrentReminder({
                id: null,
                title: '',
                description: '',
                due_date: format(date, 'yyyy-MM-dd'),
            });
        }
        setModalVisible(true);
    };

    const handleCloseModal = () => {
        setModalVisible(false);
        setCurrentReminder({ id: null, title: '', description: '', due_date: format(date, 'yyyy-MM-dd') });
    };

    const handleModalSave = async (e) => {
        e.preventDefault();
        if (!currentReminder.title || !currentReminder.due_date) return;

        setIsSubmitting(true);
        try {
            const payload = {
                title: currentReminder.title,
                description: currentReminder.description,
                due_date: currentReminder.due_date,
            };

            if (isEditing) {
                await remindersAPI.update(currentReminder.id, payload);
            } else {
                await remindersAPI.create(payload);
            }
            handleCloseModal();
            await fetchReminders();
        } catch (err) {
            alert("Kaydedilemedi: " + (err.response?.data?.message || err.message));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Bu hatırlatıcıyı silmek istediğinize emin misiniz?')) return;
        setActionLoadingId(`delete-${id}`);
        try {
            await remindersAPI.delete(id);
            await fetchReminders();
        } catch (err) {
            alert("Silinemedi: " + (err.response?.data?.message || err.message));
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleComplete = async (id) => {
        setActionLoadingId(`complete-${id}`);
        try {
            await remindersAPI.markAsCompleted(id);
            await fetchReminders();
        } catch (err) {
            alert("Durum güncellenemedi: " + (err.response?.data?.message || err.message));
        } finally {
            setActionLoadingId(null);
        }
    };

    // --- RENDER ---
    if (loading) return <RemindersSkeleton />;

    return (
        <div className="mt-5 flex w-full flex-col gap-6">

            {/* --- SAYFA BAŞLIĞI VE AKSİYON --- */}
            <div className="flex flex-col md:flex-row md:items-center justify-between rounded-2xl bg-white p-6 shadow-3xl shadow-shadow-500 dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10">
                <div>
                    <h2 className="text-2xl font-bold text-navy-700 dark:text-white flex items-center gap-2">
                        <MdCalendarToday className="text-brand-500" /> Görevler & Hatırlatıcılar
                    </h2>
                    <p className="mt-1 text-sm font-medium text-gray-500 dark:text-gray-400">
                        Kişisel ve ekibe ait önemli tarihleri buradan yönetebilirsiniz.
                    </p>
                </div>
                <button
                    onClick={() => handleOpenModal()}
                    className="mt-4 md:mt-0 flex items-center justify-center gap-2 rounded-xl bg-brand-500 px-6 py-3 text-sm font-bold text-white transition-all hover:bg-brand-600 active:scale-95 shadow-lg shadow-brand-500/30"
                >
                    <MdAdd className="text-xl" /> Yeni Ekle
                </button>
            </div>

            {/* --- ANA İÇERİK GRID --- */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                {/* SOL: TAKVİM KARTI */}
                <div className="lg:col-span-4 flex flex-col rounded-3xl bg-white p-6 shadow-3xl shadow-shadow-500 dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10">
                    <h4 className="mb-4 text-lg font-bold text-navy-700 dark:text-white">Tarih Seçin</h4>
                    <div className="custom-calendar-wrapper w-full">
                        <Calendar
                            onChange={handleDateChange}
                            value={date}
                            tileContent={tileContent}
                            locale="tr-TR"
                            className="w-full border-none bg-transparent font-sans"
                        />
                    </div>
                </div>

                {/* SAĞ: HATIRLATICILAR LİSTESİ */}
                <div className="lg:col-span-8 flex flex-col rounded-3xl bg-white p-6 shadow-3xl shadow-shadow-500 dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10">

                    <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between border-b border-gray-100 dark:border-white/10 pb-4">
                        <h4 className="text-xl font-bold text-navy-700 dark:text-white flex items-center gap-2">
                            <span className="text-brand-500">{format(date, 'd MMMM yyyy', { locale: tr })}</span>
                            <span className="text-sm font-medium text-gray-400">Tarihli Görevler</span>
                        </h4>
                        <div className="mt-2 sm:mt-0 rounded-full bg-gray-100 dark:bg-navy-700 px-4 py-1 text-sm font-bold text-navy-700 dark:text-white">
                            Toplam: {selectedDateReminders.length}
                        </div>
                    </div>

                    {/* LİSTE */}
                    <div className="flex flex-col gap-4 min-h-[300px]">
                        {selectedDateReminders.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full opacity-50 py-10">
                                <MdInfoOutline className="text-6xl text-gray-400 mb-4" />
                                <p className="text-lg font-bold text-gray-500 dark:text-gray-400">Bu tarihte planlanmış bir görev yok.</p>
                            </div>
                        ) : (
                            currentItems.map((reminder) => (
                                <div
                                    key={reminder.id}
                                    className={`relative flex flex-col md:flex-row md:items-center justify-between gap-4 overflow-hidden rounded-2xl border p-5 shadow-sm transition-all hover:shadow-md ${
                                        reminder.status === 'completed'
                                            ? 'border-gray-200 bg-gray-50 dark:border-white/5 dark:bg-navy-900/50 opacity-70'
                                            : 'border-brand-100 bg-white dark:border-brand-400/20 dark:bg-navy-800'
                                    }`}
                                >
                                    {/* Renk Çizgisi */}
                                    <div className={`absolute left-0 top-0 h-full w-1.5 ${reminder.status === 'completed' ? 'bg-gray-300 dark:bg-gray-600' : 'bg-brand-500 dark:bg-brand-400'}`}></div>

                                    {/* İçerik */}
                                    <div className="ml-2 flex-1">
                                        <div className="flex flex-wrap items-center gap-2 mb-1">
                                            <h5 className={`text-base font-bold ${reminder.status === 'completed' ? 'text-gray-500 line-through' : 'text-navy-700 dark:text-white'}`}>
                                                {reminder.title}
                                            </h5>
                                            <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                                                reminder.status === 'completed' ? 'bg-gray-200 text-gray-600 dark:bg-navy-700 dark:text-gray-400' : 'bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-400'
                                            }`}>
                        {reminder.status === 'completed' ? 'Tamamlandı' : 'Bekliyor'}
                      </span>
                                        </div>
                                        {reminder.description && (
                                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 line-clamp-2">
                                                {reminder.description}
                                            </p>
                                        )}
                                    </div>

                                    {/* Aksiyon Butonları (Mobilde Alt Alta, Desktopta Yanyana) */}
                                    <div className="flex flex-wrap items-center gap-2">
                                        {reminder.status !== 'completed' && (
                                            <button
                                                onClick={() => handleComplete(reminder.id)}
                                                disabled={actionLoadingId === `complete-${reminder.id}`}
                                                title="Tamamla"
                                                className="flex items-center justify-center h-10 w-10 rounded-xl bg-green-50 text-green-600 hover:bg-green-500 hover:text-white dark:bg-green-500/10 dark:text-green-400 dark:hover:bg-green-500 dark:hover:text-white transition-all disabled:opacity-50"
                                            >
                                                {actionLoadingId === `complete-${reminder.id}` ? <MdWarning className="animate-spin" /> : <MdCheckCircle className="text-xl" />}
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleOpenModal(reminder)}
                                            title="Düzenle"
                                            className="flex items-center justify-center h-10 w-10 rounded-xl bg-gray-50 text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600 transition-all"
                                        >
                                            <MdEdit className="text-xl" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(reminder.id)}
                                            disabled={actionLoadingId === `delete-${reminder.id}`}
                                            title="Sil"
                                            className="flex items-center justify-center h-10 w-10 rounded-xl bg-red-50 text-red-600 hover:bg-red-500 hover:text-white dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500 dark:hover:text-white transition-all disabled:opacity-50"
                                        >
                                            {actionLoadingId === `delete-${reminder.id}` ? <MdWarning className="animate-spin" /> : <MdDelete className="text-xl" />}
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* --- SAYFALAMA (PAGINATION) --- */}
                    {totalPages > 1 && (
                        <div className="mt-6 flex items-center justify-between border-t border-gray-100 dark:border-white/10 pt-4">
                            <p className="text-sm font-medium text-gray-500">
                                Toplam <span className="font-bold text-navy-700 dark:text-white">{selectedDateReminders.length}</span> kayıttan <span className="font-bold text-navy-700 dark:text-white">{indexOfFirstItem + 1}-{Math.min(indexOfLastItem, selectedDateReminders.length)}</span> arası gösteriliyor.
                            </p>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700 transition-all"
                                >
                                    <MdChevronLeft className="text-xl" />
                                </button>
                                <div className="flex h-8 min-w-[32px] items-center justify-center rounded-lg bg-brand-500 px-2 text-sm font-bold text-white shadow-md">
                                    {currentPage}
                                </div>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700 transition-all"
                                >
                                    <MdChevronRight className="text-xl" />
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* --- NATIVE TAILWIND MODAL (EKLE / DÜZENLE) --- */}
            {modalVisible && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm transition-all">
                    <div className="relative w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl dark:bg-navy-800 sm:p-8">

                        {/* Modal Header */}
                        <div className="mb-6 flex items-center justify-between border-b border-gray-100 pb-4 dark:border-white/10">
                            <h3 className="text-xl font-bold text-navy-700 dark:text-white">
                                {isEditing ? 'Hatırlatıcıyı Düzenle' : 'Yeni Hatırlatıcı'}
                            </h3>
                            <button onClick={handleCloseModal} className="rounded-full p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-navy-700 transition-all">
                                <MdClose className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Modal Body (Form) */}
                        <form onSubmit={handleModalSave} className="flex flex-col gap-5">

                            <div>
                                <label className="mb-1.5 block text-sm font-bold text-navy-700 dark:text-gray-300">Başlık *</label>
                                <input
                                    required
                                    type="text"
                                    placeholder="Örn: Kira Ödemesi"
                                    value={currentReminder.title}
                                    onChange={e => setCurrentReminder({...currentReminder, title: e.target.value})}
                                    className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400 transition-all"
                                />
                            </div>

                            <div>
                                <label className="mb-1.5 block text-sm font-bold text-navy-700 dark:text-gray-300">Tarih *</label>
                                <input
                                    required
                                    type="date"
                                    value={currentReminder.due_date}
                                    onChange={e => setCurrentReminder({...currentReminder, due_date: e.target.value})}
                                    className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400 transition-all"
                                />
                            </div>

                            <div>
                                <label className="mb-1.5 block text-sm font-bold text-navy-700 dark:text-gray-300">Açıklama (Opsiyonel)</label>
                                <textarea
                                    rows="3"
                                    placeholder="Detaylı notlar..."
                                    value={currentReminder.description}
                                    onChange={e => setCurrentReminder({...currentReminder, description: e.target.value})}
                                    className="flex w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:text-white dark:focus:border-brand-400 transition-all resize-none"
                                />
                            </div>

                            {/* Modal Footer */}
                            <div className="mt-2 flex items-center justify-end gap-3 border-t border-gray-100 pt-5 dark:border-white/10">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    disabled={isSubmitting}
                                    className="rounded-xl px-5 py-2.5 text-sm font-bold text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-700 transition-all"
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="flex items-center justify-center rounded-xl bg-brand-500 px-6 py-2.5 text-sm font-bold text-white transition-all hover:bg-brand-600 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-brand-500/30"
                                >
                                    {isSubmitting ? <MdWarning className="animate-spin h-5 w-5" /> : 'Kaydet'}
                                </button>
                            </div>

                        </form>
                    </div>
                </div>
            )}

            {/* --- HORIZON UI ÖZEL TAKVİM STİLLERİ (TAILWIND ENJEKSİYONU) --- */}
            <style jsx="true">{`
        .custom-calendar-wrapper .react-calendar {
          width: 100% !important;
          border: none !important;
          background: transparent !important;
          font-family: inherit !important;
        }
        
        .custom-calendar-wrapper .react-calendar__navigation {
          display: flex;
          margin-bottom: 1rem;
          align-items: center;
        }
        
        .custom-calendar-wrapper .react-calendar__navigation button {
          color: inherit;
          min-width: 44px;
          background: none;
          font-weight: bold;
          font-size: 1rem;
          border-radius: 12px;
          padding: 8px;
          transition: all 0.2s ease;
        }
        
        .custom-calendar-wrapper .react-calendar__navigation button:hover {
          background-color: rgba(67, 24, 255, 0.05) !important;
          color: #4318FF !important;
        }

        .dark .custom-calendar-wrapper .react-calendar__navigation button {
          color: white;
        }
        .dark .custom-calendar-wrapper .react-calendar__navigation button:hover {
          background-color: rgba(255, 255, 255, 0.1) !important;
          color: white !important;
        }

        .custom-calendar-wrapper .react-calendar__month-view__weekdays {
          text-transform: uppercase;
          font-weight: bold;
          font-size: 0.75rem;
          color: #A3AED0;
          text-align: center;
          margin-bottom: 0.5rem;
        }
        
        .custom-calendar-wrapper .react-calendar__month-view__days__day {
          padding: 10px 6px;
          background: none;
          border: 0;
          outline: none;
          color: #2B3674;
          font-weight: 600;
          border-radius: 12px;
          transition: all 0.2s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 48px;
        }
        
        .dark .custom-calendar-wrapper .react-calendar__month-view__days__day {
          color: white;
        }
        
        .custom-calendar-wrapper .react-calendar__month-view__days__day--neighboringMonth {
          color: #A3AED0 !important;
          opacity: 0.4;
        }

        .custom-calendar-wrapper .react-calendar__tile:hover {
          background-color: rgba(67, 24, 255, 0.05) !important;
        }
        .dark .custom-calendar-wrapper .react-calendar__tile:hover {
          background-color: rgba(255, 255, 255, 0.05) !important;
        }

        /* Seçili Gün Stili */
        .custom-calendar-wrapper .react-calendar__tile--active {
          background-color: #4318FF !important;
          color: white !important;
          box-shadow: 0 4px 15px rgba(67, 24, 255, 0.4);
        }
        
        /* Bugünün Stili */
        .custom-calendar-wrapper .react-calendar__tile--now {
          background-color: rgba(67, 24, 255, 0.1);
          color: #4318FF;
        }
        .dark .custom-calendar-wrapper .react-calendar__tile--now {
          background-color: rgba(255, 255, 255, 0.1);
          color: white;
        }
        .custom-calendar-wrapper .react-calendar__tile--now.react-calendar__tile--active {
          background-color: #4318FF !important;
          color: white !important;
        }
      `}</style>
        </div>
    );
};

export default CommonReminders;